import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

/**
 * #121 CSS 媒体状态动画 (Media State Animations)
 * 
 * 基于 WebKit Interop 2026 新增的 CSS 媒体状态伪类概念
 * 模拟 :playing, :paused, :buffering 等状态的动画效果
 */
export const MediaStateAnimations: React.FC<{
  text?: string;
  state?: 'playing' | 'paused' | 'buffering';
}> = ({ text = 'MEDIA', state = 'playing' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 根据状态设置不同的动画
  const stateStyles = {
    playing: {
      scale: spring({ frame, fps, config: { damping: 100, stiffness: 200 } }),
      rotation: Math.sin(frame * 0.1) * 5,
      opacity: interpolate(
        Math.sin(frame * 0.05),
        [-1, 1],
        [0.8, 1]
      ),
      color: '#4ade80',
      shadow: `0 0 ${20 + Math.sin(frame * 0.1) * 10}px rgba(74, 222, 128, 0.8)`,
    },
    paused: {
      scale: 1,
      rotation: 0,
      opacity: 0.6,
      color: '#94a3b8',
      shadow: '0 0 0px rgba(148, 163, 184, 0)',
    },
    buffering: {
      scale: interpolate(
        Math.sin(frame * 0.3),
        [-1, 1],
        [0.95, 1.05]
      ),
      rotation: 0,
      opacity: interpolate(
        Math.abs(Math.sin(frame * 0.2)),
        [0, 1],
        [0.4, 0.9]
      ),
      color: '#fbbf24',
      shadow: `0 0 ${15 + Math.sin(frame * 0.2) * 5}px rgba(251, 191, 36, 0.6)`,
    },
  };

  const currentStyle = stateStyles[state];

  // 进度条动画（模拟播放进度）
  const progress = interpolate(frame, [0, 150], [0, 100], {
    extrapolateRight: 'clamp',
  });

  // 缓冲指示器（loading dots）
  const bufferingDots = [0, 1, 2].map((i) => (
    <div
      key={i}
      style={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: '#fbbf24',
        opacity: interpolate(
          Math.sin((frame + i * 10) * 0.3),
          [-1, 1],
          [0.3, 1]
        ),
        transform: `scale(${interpolate(
          Math.sin((frame + i * 10) * 0.3),
          [-1, 1],
          [0.8, 1.2]
        )})`,
      }}
    />
  ));

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f172a',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 主文字 */}
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          color: currentStyle.color,
          textShadow: currentStyle.shadow,
          transform: `scale(${currentStyle.scale}) rotate(${currentStyle.rotation}deg)`,
          opacity: currentStyle.opacity,
          marginBottom: 60,
        }}
      >
        {text}
      </div>

      {/* 状态指示器 */}
      <div
        style={{
          fontSize: 32,
          color: currentStyle.color,
          marginBottom: 40,
          opacity: currentStyle.opacity,
        }}
      >
        {state.toUpperCase()}
      </div>

      {/* 进度条（playing 状态） */}
      {state === 'playing' && (
        <div
          style={{
            width: 600,
            height: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: currentStyle.color,
              boxShadow: currentStyle.shadow,
            }}
          />
        </div>
      )}

      {/* 缓冲指示器（buffering 状态） */}
      {state === 'buffering' && (
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 20,
          }}
        >
          {bufferingDots}
        </div>
      )}

      {/* 暂停图标（paused 状态） */}
      {state === 'paused' && (
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginTop: 20,
          }}
        >
          <div
            style={{
              width: 16,
              height: 60,
              backgroundColor: currentStyle.color,
              opacity: currentStyle.opacity,
            }}
          />
          <div
            style={{
              width: 16,
              height: 60,
              backgroundColor: currentStyle.color,
              opacity: currentStyle.opacity,
            }}
          />
        </div>
      )}

      {/* 状态说明文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          fontSize: 20,
          color: 'rgba(255, 255, 255, 0.5)',
          fontFamily: 'monospace',
        }}
      >
        CSS Media State Pseudo-classes 2026
      </div>
    </AbsoluteFill>
  );
};

/**
 * #122 Web Animations API 高级动画 (WAAPI Advanced)
 * 
 * 基于 Web Animations API 的高性能动画
 * 模拟 Motion One 库的复杂动画序列
 */
export const WAAPIAdvanced: React.FC<{
  text?: string;
}> = ({ text = 'WAAPI' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 多层动画组合
  const layers = [
    {
      name: 'Scale',
      value: spring({ frame, fps, config: { damping: 12, stiffness: 100 } }),
      min: 0,
      max: 1,
      color: '#60a5fa',
      delay: 0,
    },
    {
      name: 'Rotation',
      value: interpolate(frame, [0, 60, 120], [0, 180, 360], {
        extrapolateRight: 'clamp',
      }),
      min: 0,
      max: 360,
      color: '#a78bfa',
      delay: 15,
    },
    {
      name: 'Opacity',
      value: interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' }),
      min: 0,
      max: 1,
      color: '#f472b6',
      delay: 30,
    },
    {
      name: 'Translate',
      value: interpolate(frame, [0, 60, 120], [-100, 0, 100], {
        extrapolateRight: 'clamp',
      }),
      min: -100,
      max: 100,
      color: '#34d399',
      delay: 45,
    },
  ];

  // 主文字动画
  const textScale = spring({ frame, fps, config: { damping: 15, stiffness: 150 } });
  const textRotation = interpolate(frame, [0, 120], [0, 360 * 3], {
    extrapolateRight: 'clamp',
  });

  // 关键帧可视化
  const keyframes = [0, 30, 60, 90, 120, 150];
  const currentKeyframe = keyframes.findIndex((kf) => frame < kf);
  const progress = currentKeyframe >= 0 ? currentKeyframe / keyframes.length : 1;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#1e293b',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* 主文字 - 3D 旋转 */}
      <div
        style={{
          fontSize: 100,
          fontWeight: 'bold',
          color: '#fff',
          transform: `scale(${textScale}) rotate(${textRotation}deg)`,
          textShadow: '0 0 40px rgba(96, 165, 250, 0.8)',
          marginBottom: 80,
        }}
      >
        {text}
      </div>

      {/* 动画属性条 */}
      <div
        style={{
          width: 800,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {layers.map((layer, index) => {
          const normalizedValue = (layer.value - layer.min) / (layer.max - layer.min);
          const layerOpacity = spring({
            frame: frame - layer.delay,
            fps,
            config: { damping: 100, stiffness: 200 },
          });

          return (
            <div key={index} style={{ opacity: layerOpacity }}>
              {/* 属性名称 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <span style={{ color: layer.color, fontSize: 18, fontWeight: 'bold' }}>
                  {layer.name}
                </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 16 }}>
                  {layer.value.toFixed(1)}
                </span>
              </div>

              {/* 进度条 */}
              <div
                style={{
                  width: '100%',
                  height: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${normalizedValue * 100}%`,
                    height: '100%',
                    backgroundColor: layer.color,
                    boxShadow: `0 0 10px ${layer.color}`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 时间轴 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          width: 800,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {keyframes.map((kf, index) => (
          <div key={index} style={{ flex: 1, textAlign: 'center' }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: frame >= kf ? '#60a5fa' : 'rgba(255, 255, 255, 0.2)',
                margin: '0 auto 8',
                boxShadow: frame >= kf ? '0 0 10px #60a5fa' : 'none',
              }}
            />
            <div
              style={{
                fontSize: 14,
                color: frame >= kf ? '#60a5fa' : 'rgba(255, 255, 255, 0.3)',
              }}
            >
              {kf}f
            </div>
          </div>
        ))}
      </div>

      {/* 当前帧指示器 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          right: 40,
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.5)',
          fontFamily: 'monospace',
        }}
      >
        Frame: {frame}
      </div>
    </AbsoluteFill>
  );
};

/**
 * #123 Canvas 生成艺术增强版 (Canvas Generative Art Enhanced)
 * 
 * 基于 Processing.js 风格的生成艺术
 * 使用 Canvas API 创建算法艺术
 */
export const CanvasGenerativeArtEnhanced: React.FC<{
  text?: string;
}> = ({ text = 'ART' }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 生成粒子
  const particleCount = 150;
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const angle = (i / particleCount) * Math.PI * 2 + frame * 0.02;
    const radius = 200 + Math.sin(frame * 0.05 + i * 0.1) * 100;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = height / 2 + Math.sin(angle) * radius;
    const size = 5 + Math.sin(frame * 0.1 + i) * 3;
    const hue = (i / particleCount) * 360 + frame;
    const opacity = 0.3 + Math.sin(frame * 0.05 + i * 0.2) * 0.4;

    return { x, y, size, hue, opacity };
  });

  // 连线
  const connections = [];
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dx = particles[i].x - particles[j].x;
      const dy = particles[i].y - particles[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 80) {
        connections.push({
          x1: particles[i].x,
          y1: particles[i].y,
          x2: particles[j].x,
          y2: particles[j].y,
          opacity: (1 - distance / 80) * 0.3,
        });
      }
    }
  }

  // 主文字动画
  const textScale = spring({ frame, fps, config: { damping: 15, stiffness: 120 } });
  const textOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f172a',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* SVG 粒子系统 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {/* 连线 */}
        {connections.map((conn, index) => (
          <line
            key={`line-${index}`}
            x1={conn.x1}
            y1={conn.y1}
            x2={conn.x2}
            y2={conn.y2}
            stroke="rgba(96, 165, 250, 0.3)"
            strokeWidth={1}
            opacity={conn.opacity}
          />
        ))}

        {/* 粒子 */}
        {particles.map((particle, index) => (
          <circle
            key={`particle-${index}`}
            cx={particle.x}
            cy={particle.y}
            r={particle.size}
            fill={`hsla(${particle.hue}, 70%, 60%, ${particle.opacity})`}
          />
        ))}
      </svg>

      {/* 中心文字 */}
      <div
        style={{
          fontSize: 120,
          fontWeight: 'bold',
          color: '#fff',
          textShadow: '0 0 60px rgba(96, 165, 250, 1)',
          transform: `scale(${textScale})`,
          opacity: textOpacity,
          zIndex: 10,
        }}
      >
        {text}
      </div>

      {/* 副标题 */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          fontSize: 24,
          color: 'rgba(255, 255, 255, 0.6)',
          fontFamily: 'monospace',
        }}
      >
        Generative Art • {particleCount} Particles • Canvas API
      </div>

      {/* 性能统计 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 40,
          fontSize: 16,
          color: 'rgba(255, 255, 255, 0.4)',
          fontFamily: 'monospace',
        }}
      >
        <div>Particles: {particleCount}</div>
        <div>Connections: {connections.length}</div>
        <div>Frame: {frame}</div>
      </div>
    </AbsoluteFill>
  );
};
